import { getFloat16, setFloat16 } from '@petamoriken/float16';

export type SetupGraphNodeValueTypes =
  | 'i8'
  | 'i16'
  | 'i32'
  | 'u8'
  | 'u16'
  | 'u32'
  | 'f16'
  | 'f32'
  | 'h8'
  | 'h16'
  | 'h32';

export type SetupGraphNodeValueStringTypes = `h8` | 'h16' | 'h32';

export const isUnsignedType = (type: SetupGraphNodeValueTypes) => {
  return type.startsWith('u') || type.startsWith('h');
};

export type SetupGraphNodeValueOptions = {
  byteOffset?: number;
};
/**
 * SetupGraphNodeValue is a class that represents a value of a SetupGraphNode.
 * It can be a float, an int, an unsigned int or a hex.
 * It is used to read and write values from a DataView.
 * @example
 * ```ts
 * const buffer = new ArrayBuffer(4);
 * const view = new DataView(buffer);
 * const value = new SetupGraphNodeValue(view, 'f32');
 * value.value = 1.12331245;
 * console.log(value.value); // 1.12331245
 * ```
 */
export class SetupGraphNodeValue<TType extends SetupGraphNodeValueTypes> {
  static defaultType: SetupGraphNodeValueTypes = 'u32';

  type: SetupGraphNodeValueTypes = SetupGraphNodeValue.defaultType;
  byteOffset: number = 0;
  view: DataView;

  constructor(mixed: DataView | number | string, type?: TType, byteOffset?: number) {
    this.byteOffset = byteOffset ?? this.byteOffset;
    this.type = type ?? this.type;

    if (mixed instanceof DataView) {
      this.view = mixed;
    } else {
      this.view = new DataView(new ArrayBuffer(this.byteLength + this.byteOffset));
      this.value = mixed as TType extends SetupGraphNodeValueStringTypes ? string : number;
    }
  }

  static getByteLength = (type: SetupGraphNodeValueTypes): number => {
    switch (type) {
      case 'i8':
      case 'u8':
      case 'h8':
        return 1;
      case 'i16':
      case 'u16':
      case 'f16':
      case 'h16':
        return 2;
      case 'i32':
      case 'u32':
      case 'f32':
      case 'h32':
        return 4;
      default:
        return 4;
    }
  };

  get byteLength() {
    return SetupGraphNodeValue.getByteLength(this.type);
  }

  set value(value: TType extends SetupGraphNodeValueStringTypes ? string : number) {
    switch (this.type) {
      case 'u8':
        this.view.setUint8(this.byteOffset, value as number);
        break;
      case 'i8':
        this.view.setInt8(this.byteOffset, value as number);
        break;
      case 'u16':
        this.view.setUint16(this.byteOffset, value as number);
        break;
      case 'i16':
        this.view.setInt16(this.byteOffset, value as number);
        break;
      case 'u32':
        this.view.setUint32(this.byteOffset, value as number);
        break;
      case 'i32':
        this.view.setInt32(this.byteOffset, value as number);
        break;
      case 'f32':
        this.view.setFloat32(this.byteOffset, value as number);
        break;
      case 'f16':
        setFloat16(this.view, this.byteOffset, value as number);
        break;
      case 'h8':
        this.view.setUint8(this.byteOffset, parseInt(value as string, 16));
        break;
      case 'h16':
        this.view.setUint16(this.byteOffset, parseInt(value as string, 16));
        break;
      case 'h32':
        this.view.setUint32(this.byteOffset, parseInt(value as string, 16));
        break;
      default:
        throw new Error(`Invalid byte length: ${this.byteLength}`);
    }
  }

  get value(): TType extends SetupGraphNodeValueStringTypes ? string : number {
    let value: number | string = 0;

    switch (this.type) {
      case 'u8':
        value = this.view.getUint8(this.byteOffset);
        break;
      case 'i8':
        value = this.view.getInt8(this.byteOffset);
        break;
      case 'u16':
        value = this.view.getUint16(this.byteOffset);
        break;
      case 'i16':
        value = this.view.getInt16(this.byteOffset);
        break;
      case 'u32':
        value = this.view.getUint32(this.byteOffset);
        break;
      case 'i32':
        value = this.view.getInt32(this.byteOffset);
        break;
      case 'f32':
        value = this.view.getFloat32(this.byteOffset);
        break;
      case 'f16':
        if (this.byteOffset + 1 >= this.view.byteLength) {
          throw new Error('Trying to read beyond the DataView size');
        }
        value = getFloat16(this.view, this.byteOffset);
        break;
      case 'h8':
        value = `0x${this.view.getUint8(this.byteOffset).toString(16)}`;
        break;
      case 'h16':
        value = `0x${this.view.getUint16(this.byteOffset).toString(16)}`;
        break;
      case 'h32':
        value = `0x${this.view.getUint32(this.byteOffset).toString(16)}`;
        break;
      default:
        throw new Error(`Invalid byte length: ${this.byteLength}`);
    }
    return value as TType extends SetupGraphNodeValueStringTypes ? string : number;
  }
}

export default SetupGraphNodeValue;
